import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { useTranslate } from "@similarweb/i18n-provider";
import type { ConversionRatesCategoryLeadersListParams } from "@similarweb/pro-api-client";
import { formatDate, fullMonthAndYear } from "@similarweb/pro-logic-components";
import { Flex } from "@similarweb/ui-components/foundations";

import type { ICategory, ICustomCategory } from "app/common/services/categoryService.types";
import { useCategoriesComparison } from "app/pages/industry-analysis/categories-context/CategoriesContext";
import CountryService from "app/services/CountryService";
import DurationService, { BasicDurations } from "app/services/DurationService";

import { CONVERSION_RATE_TRANSLATION_KEYS } from "../../consts";
import { getConversionRateAvailability } from "../../shared/getConversionRateAvailability";

import { ConversionRateLeadersTable } from "./ConversionRateLeadersTable";
import { getRateLeadersColumnConfig, getVolumeLeadersColumnConfig } from "./getLeadersColumnConfig";
import { useConversionRateLeadersData } from "./useConversionRateLeadersData";

const VOLUME_AUTOMATION_ID = "conversion-rate-volume-leaders-card";
const RATE_AUTOMATION_ID = "conversion-rate-rate-leaders-card";

interface IConversionRateLeadersContentProps {
    primaryCategory: ICategory | ICustomCategory;
}

const ConversionRateLeadersContent = ({ primaryCategory }: IConversionRateLeadersContentProps) => {
    const { translate } = useTranslate();
    const { params } = useCurrentStateAndParams();

    const country = params.country as number;
    const includeSubdomains = (params.includeSubdomains as boolean | undefined) ?? true;
    const durationParam = (params.duration as string) ?? BasicDurations.LAST_MONTH;
    const {
        iso: { from, to },
        isWindow,
    } = DurationService.getDurationData(durationParam, params.comparedDuration as string).forAPI;

    const countryText = CountryService.getCountryById(country)?.text ?? "";
    const dateRangeText = formatDate(from, to, fullMonthAndYear);

    const apiParams = {
        category: primaryCategory.forApi,
        country: country,
        includeSubdomains: includeSubdomains,
        from,
        to,
        isWindow,
    } satisfies ConversionRatesCategoryLeadersListParams;

    const { data, isLoading } = useConversionRateLeadersData(apiParams, { enabled: true });

    const volumeRows = data?.volumeLeaders ?? [];
    const rateRows = data?.rateLeaders ?? [];

    const volumeColumnConfig = useMemo(
        () => getVolumeLeadersColumnConfig(translate, VOLUME_AUTOMATION_ID),
        [translate],
    );
    const rateColumnConfig = useMemo(() => getRateLeadersColumnConfig(translate, RATE_AUTOMATION_ID), [translate]);

    const hasLeaders = volumeRows.length > 0 && rateRows.length > 0;

    if (!isLoading && !hasLeaders) {
        return null;
    }

    return (
        <Flex
            gap="24px"
            width="100%"
            data-automation="conversion-rate-leaders-section"
        >
            <ConversionRateLeadersTable
                automationId={VOLUME_AUTOMATION_ID}
                title={translate(CONVERSION_RATE_TRANSLATION_KEYS.topByVolumeTitle)}
                tooltip={translate(CONVERSION_RATE_TRANSLATION_KEYS.topByVolumeTooltip)}
                dateRangeText={dateRangeText}
                countryCode={country}
                countryText={countryText}
                isLoading={isLoading}
                rows={volumeRows}
                columnConfig={volumeColumnConfig}
                trackingSubName="Converted Visits"
                primaryCategory={primaryCategory}
            />
            <ConversionRateLeadersTable
                automationId={RATE_AUTOMATION_ID}
                title={translate(CONVERSION_RATE_TRANSLATION_KEYS.topByRateTitle)}
                tooltip={translate(CONVERSION_RATE_TRANSLATION_KEYS.topByRateTooltip)}
                dateRangeText={dateRangeText}
                countryCode={country}
                countryText={countryText}
                isLoading={isLoading}
                rows={rateRows}
                columnConfig={rateColumnConfig}
                trackingSubName="Conversion Rate"
                primaryCategory={primaryCategory}
            />
        </Flex>
    );
};

export const ConversionRateLeaders = () => {
    const availabilityState = getConversionRateAvailability();
    const { primaryCategory, isComparisonMode } = useCategoriesComparison();

    if (
        availabilityState !== "available" ||
        !primaryCategory ||
        isComparisonMode ||
        DurationService.isPeriodOverPeriod()
    ) {
        return null;
    }

    return <ConversionRateLeadersContent primaryCategory={primaryCategory} />;
};
